import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [info, setInfo] = useState(null);
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error"); return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setInfo(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); refreshUser(); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
      } catch (e) {
        // Network blip during polling — log so we can investigate failed payments.
        // Don't show to user; the retry loop will surface a "timeout" status if it persists.
        console.warn("Payment status poll failed", e?.response?.status, e?.message);
      }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <PhoneFrame>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="text-[#2fbf71] animate-spin" />
            <div className="ps-heading text-2xl font-bold mt-5">Verifying payment…</div>
            <p className="text-slate-400 mt-2">Please wait a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} className="text-[#2fbf71]" />
            <div className="ps-heading text-3xl font-bold mt-5" data-testid="payment-success-heading">Payment Successful!</div>
            <p className="text-slate-400 mt-2">Thank you for supporting your wellness journey.</p>
            {info && <div className="text-xs text-slate-500 mt-3">${(info.amount_total / 100).toFixed(2)} {info.currency?.toUpperCase()}</div>}
            <button onClick={() => nav("/home")} className="ps-button-primary mt-6 w-full" data-testid="payment-back-home">Back to Home</button>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle size={48} className="text-[#EF4444]" />
            <div className="ps-heading text-2xl font-bold mt-5">Payment {status}</div>
            <p className="text-slate-400 mt-2">If you were charged, please contact support.</p>
            <button onClick={() => nav("/home")} className="ps-button-secondary mt-6 w-full">Back to Home</button>
          </>
        )}
      </div>
    </PhoneFrame>
  );
}
